import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import DraggableStepper from "../ui/draggable-stepper";
import { WorkoutContext } from "../../contexts/WorkoutContext";
import { useUserProfile } from "../../contexts/UserProfileContext";
import { useAuth } from "../../contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import SegmentedToggle from "../ui/segmented-toggle";
import {
  resolveSegmentMode,
  segmentFlagsForMode,
} from "../../utils/workoutPlanUtils";
import { carriedConfigForPlan } from "../../utils/generationCarryOver";
import useSessionRecipes from "../../hooks/useSessionRecipes";
import useSessionShapePreview from "../../hooks/useSessionShapePreview";
import HelpLink from "../help/HelpLink";
import useMediaQuery from "../../hooks/useMediaQuery";
import {
  buildRecipeConfig,
  expandRecipeConfig,
  suggestPinnedFields,
} from "../../utils/sessionRecipes";
import {
  AlertTriangle,
  Check,
  Dumbbell,
  Layers,
  MessageSquareText,
  PersonStanding,
  Plus,
  Settings,
  Sparkles,
  Timer,
  Wand2,
} from "lucide-react";
import { EQUIPMENT_LOCATION_MAP } from "../../config/equipment";
import { normaliseStyleSelection } from "../../utils/sessionFormat";
import {
  formatSummary,
  kitSummary,
  musclesSummary,
  notesSummary,
  timingRowSummary,
} from "../../utils/generateSummaries";
import { getWorkoutSettings } from "../../hooks/useWorkoutSettings";
import TrainForPicker from "./generate/TrainForPicker";
import PickCard from "./generate/PickCard";
import DetailRows from "./generate/DetailRows";
import DetailSheet, { DESKTOP_QUERY } from "./generate/DetailSheet";
import SessionSummary from "./generate/SessionSummary";
import ModelSwitcher, { useProviderStatus } from "./generate/ModelSwitcher";
import GenerationProgress from "./generate/GenerationProgress";
import FormatDetail from "./generate/details/FormatDetail";
import MusclesDetail from "./generate/details/MusclesDetail";
import KitDetail from "./generate/details/KitDetail";
import TimingDetail from "./generate/details/TimingDetail";
import NotesDetail from "./generate/details/NotesDetail";
import RecipeSaveDetail from "./generate/details/RecipeSaveDetail";
import { MoreOptionsRow } from "../profile/ProfileParts";
import useUiMode from "../../hooks/useUiMode";

// Old selections put formats (Circuit, Functional) in the goal list; the form
// works in goals + format (#83 L4), so every config entering it is normalised.
const withStyleSelection = (config) => ({
  ...config,
  ...normaliseStyleSelection(config),
});

const READINESS_OPTIONS = [
  { value: "Low Energy", label: "Low" },
  { value: "Normal", label: "Normal" },
  { value: "Feeling Great", label: "High" },
];

// A sparse override must never blank out a field it says nothing about, so
// undefined/null entries are dropped before layering. `[]` and `0` survive:
// they are real settings (an empty kit list means bodyweight only).
const dropEmptyOverrides = (overrides) =>
  Object.fromEntries(
    Object.entries(overrides || {}).filter(
      ([, v]) => v !== undefined && v !== null,
    ),
  );

const normaliseSelectionList = (value) => {
  if (Array.isArray(value)) return [...value];
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

// Tier 3 (Contextual / "implicit sticky") persistence: the training location
// rarely changes day-to-day, so we remember the last one the user picked in
// localStorage and rehydrate it on mount. Wrapped in try/catch for private-mode /
// quota safety. (sessionNotes + readiness are Tier 1 ephemeral; sessionDuration,
// primaryGoal + workoutTypes are Tier 2 read-only profile defaults — none of those
// are persisted from this component.)
const LAST_LOCATION_KEY = "khronos_last_location";

const readLastLocation = () => {
  try {
    return localStorage.getItem(LAST_LOCATION_KEY) || null;
  } catch {
    return null;
  }
};

const writeLastLocation = (value) => {
  try {
    if (value) localStorage.setItem(LAST_LOCATION_KEY, value);
  } catch {
    /* ignore: private mode / storage disabled */
  }
};

// `contextNotes` (optional) is caller-supplied programme background passed to
// the LLM as a dedicated "Programme Context" prompt section (separate from the
// user's own session notes) — used by the programme flow to pass framework
// constraints, deload handling and surrounding-session awareness (#54).
// `configOverrides` (optional) prefills the dialog's structured knobs from a
// programme slot (duration, styles, muscle focus, goal) so the UI matches the
// plan instead of showing profile defaults; everything stays editable.
// `initialRecipeId` (optional) opens the dialog already filled from a saved
// session recipe (#78) — used when the pick happened elsewhere, e.g. the empty
// state. The dialog resolves and applies the recipe itself, so callers only
// have to hold an id.
// `prefillLabel` names where `configOverrides` came from in the prefill banner
// (defaults to the programme, the original caller).
// `autoGenerate` starts generating as soon as the dialog has filled itself in,
// so it opens on the progress view — the Session tab's Start does this.
const GenerateSessionDialog = ({
  open,
  onOpenChange,
  onNavigate,
  contextNotes,
  configOverrides,
  initialRecipeId,
  prefillLabel = "your programme",
  autoGenerate = false,
}) => {
  // Basic mode (#172): train for, time, energy, kit and notes. The finer
  // controls keep their values and show again under "More options".
  const { advanced } = useUiMode();
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    if (open) setRevealed(false);
  }, [open]);
  const showAll = advanced || revealed;
  const {
    appState,
    generatePlan,
    cancelGeneration,
    generationStatus,
    thinkingText,
    takeFirstPlan,
    userProfile,
    setUserProfile,
    workoutPlan,
    lastGenerationConfig,
    setSettingsSheet,
  } = useContext(WorkoutContext);
  const { isAuthenticated } = useAuth();
  const {
    locationPresets,
    equipmentPresets,
    addEquipmentPreset,
    updateEquipmentPreset,
    deleteEquipmentPreset,
  } = useUserProfile();
  const {
    recipes: sessionRecipes,
    saveRecipe,
    touchRecipe,
  } = useSessionRecipes();
  const [planConfig, setPlanConfig] = useState({});
  const [presetForm, setPresetForm] = useState(null);
  // Whether the form is currently showing the previous generation's answers,
  // so the banner can say so and offer a way back to plain defaults.
  const [carriedOver, setCarriedOver] = useState(false);
  // Which recipe (if any) filled the form, and the save-as form when open.
  const [activeRecipeId, setActiveRecipeId] = useState(null);
  const [recipeForm, setRecipeForm] = useState(null);
  const [savingRecipe, setSavingRecipe] = useState(false);
  // Which detail sheet is open: format | muscles | kit | timing | notes | recipe
  const [detail, setDetail] = useState(null);
  // Header divider once the form scrolls, so content never slides straight
  // under the title.
  const [scrolled, setScrolled] = useState(false);
  const hasInitialisedRef = useRef(false);
  const appliedRecipeRef = useRef(null);

  // Build location options
  const locationOptions = useMemo(() => {
    const builtIn = [
      { value: "None", label: "Bodyweight Only" },
      { value: "Home", label: "Home" },
      { value: "Gym", label: "Gym" },
      { value: "Outdoor", label: "Outdoor / Park" },
    ];

    if (!isAuthenticated || !locationPresets || locationPresets.length === 0) {
      return builtIn;
    }

    const userOptions = locationPresets.map((lp) => ({
      value: `user-${lp.id}`,
      label: lp.name,
      equipmentPresetId: lp.equipment_preset_id,
    }));

    return [
      ...userOptions,
      { value: "_divider", label: "───", disabled: true },
      ...builtIn,
    ];
  }, [isAuthenticated, locationPresets]);

  const getEquipmentForLocation = useCallback(
    (locationValue) => {
      if (locationValue?.startsWith("user-")) {
        const locId = Number(locationValue.replace("user-", ""));
        const loc = (locationPresets || []).find((lp) => lp.id === locId);
        if (loc?.equipment_preset_id) {
          const ep = (equipmentPresets || []).find(
            (p) => p.id === loc.equipment_preset_id,
          );
          if (ep?.equipment)
            return Array.isArray(ep.equipment) ? [...ep.equipment] : [];
        }
        if (loc) {
          const typeMap = {
            home: "Home",
            gym: "Gym",
            outdoor: "Outdoor",
            office: "None",
            other: "None",
          };
          return (
            EQUIPMENT_LOCATION_MAP[typeMap[loc.location_type] || "None"] || []
          );
        }
      }
      return EQUIPMENT_LOCATION_MAP[locationValue] || [];
    },
    [locationPresets, equipmentPresets],
  );

  const getDefaultLocation = useCallback(() => {
    // Tier 3 (sticky): prefer the user's last-used location, as long as it still
    // resolves — a stored "user-<id>" preset may have since been deleted.
    const stored = readLastLocation();
    if (stored) {
      const isUserPreset = stored.startsWith("user-");
      const stillValid =
        !isUserPreset ||
        (locationPresets || []).some((lp) => `user-${lp.id}` === stored);
      if (stillValid) return stored;
    }

    if (isAuthenticated && locationPresets?.length > 0) {
      const defaultLoc = locationPresets.find((lp) => lp.is_default);
      if (defaultLoc) return `user-${defaultLoc.id}`;
      return `user-${locationPresets[0].id}`;
    }
    return userProfile.trainingLocation || "Home";
  }, [isAuthenticated, locationPresets, userProfile.trainingLocation]);

  // The profile-derived baseline, with nothing layered over it. Also the
  // reference the recipe save flow diffs against to pre-tick its checklist —
  // "what about this form is not just my profile?".
  const buildProfilePlanConfig = useCallback(() => {
    const defaultLoc = getDefaultLocation();
    const locationEquipment = getEquipmentForLocation(defaultLoc);
    const defaultPreset = equipmentPresets?.find((p) => p.is_default);
    const resolvedEquipment = defaultPreset?.equipment
      ? Array.isArray(defaultPreset.equipment)
        ? [...defaultPreset.equipment]
        : []
      : locationEquipment.length > 0
        ? locationEquipment
        : normaliseSelectionList(userProfile.availableEquipment);

    return {
      sessionNotes: "",
      readiness: "Normal",
      // No faff (#85): per session, off by default; recipes can pin it on.
      noFaff: false,
      // How the session runs (#83 L4); Auto follows goals and preferences.
      format: "auto",
      // Recent-history guidance (issue #9): default to the saved setting; the
      // quick-start toggle lets the user override it for this generation only.
      useHistoryGuidance: getWorkoutSettings().guidedSessionEnabled ?? true,
      primaryGoal: userProfile.primaryGoal,
      targetAreas: normaliseSelectionList(
        userProfile.targetAreas || userProfile.targetMuscles,
      ),
      trainingLocation: defaultLoc,
      availableEquipment: resolvedEquipment,
      sessionDuration:
        userProfile.sessionDuration || userProfile.timeAvailable || 45,
      workoutTypes: normaliseSelectionList(
        userProfile.workoutTypes || userProfile.workoutStyle,
      ),
      // Warm-up / cool-down default to the tri-state saved in Settings → Workout
      // Defaults (issue #70): Off never adds one, Auto lets the AI size it, On
      // uses the fixed minutes below.
      ...segmentFlagsForMode(
        resolveSegmentMode(userProfile, "warmup"),
        "warmup",
      ),
      ...segmentFlagsForMode(
        resolveSegmentMode(userProfile, "cooldown"),
        "cooldown",
      ),
      // On Auto, the segment is only added when Time Available reaches this
      // threshold (minutes; 0 = always). Set in Settings → Workout Defaults.
      autoWarmupThreshold: userProfile.autoWarmupThreshold ?? 30,
      autoCooldownThreshold: userProfile.autoCooldownThreshold ?? 30,
      warmupPreferredDuration: userProfile.warmupPreferredDuration ?? 0,
      cooldownPreferredDuration: userProfile.cooldownPreferredDuration ?? 0,
      warmupDuration: userProfile.warmupDuration || 5,
      cooldownDuration: userProfile.cooldownDuration || 5,
    };
  }, [
    getDefaultLocation,
    getEquipmentForLocation,
    equipmentPresets,
    userProfile,
  ]);

  // The answers that generated the session on screen — what "Regenerate" should
  // open on, rather than a blank form. Null unless the stored record belongs to
  // the plan currently loaded, so a cleared or saved-and-loaded session still
  // opens on plain defaults.
  const carriedConfig = useMemo(() => {
    const carried = carriedConfigForPlan(lastGenerationConfig, workoutPlan?.id);
    if (!carried) return null;
    // A location preset can be deleted between generations. Carrying a dangling
    // "user-<id>" would leave the picker blank, so drop it (and the kit that
    // came with it) and let the profile default fill in. Only when the list has
    // actually loaded — an empty list mid-fetch must not strip a valid choice.
    const loc = carried.trainingLocation;
    if (
      typeof loc === "string" &&
      loc.startsWith("user-") &&
      locationPresets?.length > 0 &&
      !locationPresets.some((lp) => `user-${lp.id}` === loc)
    ) {
      const { trainingLocation, availableEquipment, ...rest } = carried;
      return rest;
    }
    return carried;
  }, [lastGenerationConfig, workoutPlan, locationPresets]);

  // Layered least- to most-specific: profile defaults, then the previous
  // generation's answers, then programme-slot prefills.
  const buildInitialPlanConfig = useCallback(
    () =>
      withStyleSelection({
        ...buildProfilePlanConfig(),
        ...dropEmptyOverrides(carriedConfig),
        ...dropEmptyOverrides(configOverrides),
      }),
    [buildProfilePlanConfig, carriedConfig, configOverrides],
  );

  // Back to profile defaults (plus any programme prefill), dropping the carried
  // answers — the escape hatch for "actually, start me fresh".
  const startFresh = useCallback(() => {
    setPlanConfig(
      withStyleSelection({
        ...buildProfilePlanConfig(),
        ...dropEmptyOverrides(configOverrides),
      }),
    );
    setActiveRecipeId(null);
    setCarriedOver(false);
  }, [buildProfilePlanConfig, configOverrides]);

  useEffect(() => {
    if (!open) {
      hasInitialisedRef.current = false;
      return;
    }

    if (hasInitialisedRef.current) return;

    setPlanConfig(buildInitialPlanConfig());
    setPresetForm(null);
    setRecipeForm(null);
    setActiveRecipeId(null);
    setCarriedOver(Boolean(carriedConfig));
    appliedRecipeRef.current = null;
    hasInitialisedRef.current = true;
  }, [open, buildInitialPlanConfig, carriedConfig]);

  const handleConfigChange = useCallback((key, value) => {
    setPlanConfig((prev) => ({
      ...prev,
      [key]: value,
      // Editing the style selection invalidates a Mixed archetype carried over
      // from the last generation. Without this, un-ticking a style would keep
      // the shape that selection produced — the form would say one thing and
      // the session would be built from another.
      ...(key === "workoutTypes" ? { mixedArchetypeId: null } : {}),
    }));
  }, []);

  const handleWarmupChange = useCallback(
    (val) => {
      handleConfigChange("warmupDuration", val);
    },
    [handleConfigChange],
  );

  const handleCooldownChange = useCallback(
    (val) => {
      handleConfigChange("cooldownDuration", val);
    },
    [handleConfigChange],
  );

  // Off / Auto / On (issue #70) — also refreshes the derived auto*/include* pair
  // the prompt builder reads.
  const handleSegmentModeChange = useCallback((segment, mode) => {
    setPlanConfig((prev) => ({
      ...prev,
      ...segmentFlagsForMode(mode, segment),
    }));
  }, []);

  const handleLocationChange = useCallback(
    (location) => {
      handleConfigChange("trainingLocation", location);
      handleConfigChange(
        "availableEquipment",
        getEquipmentForLocation(location),
      );
      // Tier 3: remember this choice for next time the dialog opens.
      writeLastLocation(location);
    },
    [handleConfigChange, getEquipmentForLocation],
  );

  // Surprise me / What's next: the shape is chosen here, before generating,
  // so the user can see, shuffle or hide it (#83).
  const {
    preview: shapePreview,
    shuffle: shuffleShape,
    pendingProgrammeSession,
  } = useSessionShapePreview({
    planConfig,
    userProfile,
    isAuthenticated,
    open,
    checkProgramme: !(contextNotes && contextNotes.trim()),
  });

  const handleShuffleShape = useCallback(() => {
    // A shape held over from the last generation would win every resolve;
    // shuffling is the user asking for something else.
    setPlanConfig((prev) =>
      prev.mixedArchetypeId ? { ...prev, mixedArchetypeId: null } : prev,
    );
    shuffleShape();
  }, [shuffleShape]);

  const handleHideShape = useCallback(
    (id) => {
      const hidden = userProfile?.excludedSessionShapes || [];
      if (!hidden.includes(id)) {
        setUserProfile({ ...userProfile, excludedSessionShapes: [...hidden, id] });
      }
      setPlanConfig((prev) =>
        prev.mixedArchetypeId === id ? { ...prev, mixedArchetypeId: null } : prev,
      );
    },
    [userProfile, setUserProfile],
  );

  const handleOpenProgramme = useCallback(() => {
    // Same two-frame close-then-navigate as the settings link: navigating in
    // the dialog's own commit can leave pointer-events stuck on <body>.
    onOpenChange(false);
    requestAnimationFrame(() => {
      onNavigate?.("programme");
      requestAnimationFrame(() => {
        if (document.body.style.pointerEvents === "none") {
          document.body.style.pointerEvents = "";
        }
      });
    });
  }, [onOpenChange, onNavigate]);

  const handleGenerate = useCallback(
    (e) => {
      e.preventDefault();
      // Build exactly the shape the preview showed.
      const config = shapePreview?.archetype
        ? { ...planConfig, mixedArchetypeId: shapePreview.archetype.id }
        : planConfig;
      generatePlan(
        contextNotes && contextNotes.trim()
          ? { ...config, programmeContext: contextNotes.trim() }
          : config,
      );
    },
    [generatePlan, planConfig, contextNotes, shapePreview],
  );

  // ─── Session recipes (issue #78) ─────────────────────────

  const activeRecipe = useMemo(
    () => sessionRecipes.find((r) => r.id === activeRecipeId) || null,
    [sessionRecipes, activeRecipeId],
  );

  // The prefill banner names whichever source filled the form — a programme
  // slot or a recipe — rather than always claiming it was the programme.
  const prefillSource = useMemo(() => {
    if (activeRecipe) return activeRecipe.name;
    if (configOverrides && Object.keys(configOverrides).length > 0) {
      return prefillLabel;
    }
    if (carriedOver) return "your last session";
    return null;
  }, [activeRecipe, configOverrides, carriedOver, prefillLabel]);

  /**
   * Fill the form from a recipe: profile baseline first, then the recipe's
   * pins over the top. Rebuilding from the baseline rather than merging into
   * whatever is on screen is what makes the form match the recipe exactly —
   * otherwise fields pinned by a previously-picked recipe would linger, and
   * what you saw would not be what you generated.
   */
  const applyRecipe = useCallback(
    (recipe) => {
      const overrides = expandRecipeConfig(recipe, equipmentPresets);
      setPlanConfig((prev) =>
        withStyleSelection({
          ...buildProfilePlanConfig(),
          // Per-session state is today's answer, not the recipe's business, so
          // a recipe switch keeps it — unless the recipe pins it, below.
          sessionNotes: prev.sessionNotes,
          readiness: prev.readiness,
          useHistoryGuidance: prev.useHistoryGuidance,
          ...dropEmptyOverrides(overrides),
        }),
      );
      setActiveRecipeId(recipe.id);
      // A recipe rebuilds the form from the profile baseline, so whatever was
      // carried over is gone from it.
      setCarriedOver(false);
      touchRecipe(recipe.id);
    },
    [equipmentPresets, buildProfilePlanConfig, touchRecipe],
  );

  const clearRecipe = useCallback(() => {
    setPlanConfig((prev) => ({
      ...buildInitialPlanConfig(),
      sessionNotes: prev.sessionNotes,
      readiness: prev.readiness,
      useHistoryGuidance: prev.useHistoryGuidance,
    }));
    setActiveRecipeId(null);
    setCarriedOver(Boolean(carriedConfig));
  }, [buildInitialPlanConfig, carriedConfig]);

  // Opened from a recipe picked elsewhere. Deferred until the list has loaded,
  // because a signed-in fetch can land after the dialog opens and dropping the
  // prefill would leave the user staring at plain profile defaults. The ref
  // guard means applying it once is final — clearing the recipe afterwards
  // must not spring it back.
  useEffect(() => {
    if (!open || !initialRecipeId) return;
    if (appliedRecipeRef.current === initialRecipeId) return;
    const recipe = sessionRecipes.find((r) => r.id === initialRecipeId);
    if (!recipe) return;
    appliedRecipeRef.current = initialRecipeId;
    applyRecipe(recipe);
  }, [open, initialRecipeId, sessionRecipes, applyRecipe]);

  const openSaveRecipe = useCallback(() => {
    setRecipeForm({
      name: "",
      // Pre-tick by diffing against the profile: the common case ("45 minutes
      // and kettlebells, everything else as usual") then needs one confirming
      // tap rather than nine.
      pinned: suggestPinnedFields(planConfig, buildProfilePlanConfig()),
    });
  }, [planConfig, buildProfilePlanConfig]);

  const toggleRecipeField = useCallback((key) => {
    setRecipeForm((form) => {
      if (!form) return form;
      const pinned = form.pinned.includes(key)
        ? form.pinned.filter((k) => k !== key)
        : [...form.pinned, key];
      return { ...form, pinned };
    });
  }, []);

  const handleSaveRecipe = useCallback(async () => {
    if (!recipeForm?.name.trim()) return;
    const { config, equipmentPresetId } = buildRecipeConfig(
      planConfig,
      recipeForm.pinned,
      equipmentPresets,
    );

    setSavingRecipe(true);
    try {
      const saved = await saveRecipe({
        name: recipeForm.name.trim(),
        config,
        equipment_preset_id: equipmentPresetId,
      });
      setActiveRecipeId(saved?.id ?? null);
      setRecipeForm(null);
      setDetail(null);
    } catch (err) {
      console.error("Failed to save session recipe:", err);
    } finally {
      setSavingRecipe(false);
    }
  }, [recipeForm, planConfig, equipmentPresets, saveRecipe]);

  const openSaveAsPreset = useCallback(() => {
    setPresetForm({ id: null, name: "" });
  }, []);

  const openEditPreset = useCallback(
    (preset) => {
      handleConfigChange(
        "availableEquipment",
        Array.isArray(preset.equipment) ? [...preset.equipment] : [],
      );
      setPresetForm({ id: preset.id, name: preset.name });
    },
    [handleConfigChange],
  );

  const handleSavePreset = useCallback(async () => {
    if (!presetForm?.name.trim()) return;
    const data = {
      name: presetForm.name.trim(),
      equipment: planConfig.availableEquipment || [],
      is_default: false,
    };
    try {
      if (presetForm.id) {
        await updateEquipmentPreset(presetForm.id, data);
      } else {
        await addEquipmentPreset(data);
      }
    } catch (err) {
      console.error("Failed to save equipment preset:", err);
    }
    setPresetForm(null);
  }, [
    presetForm,
    planConfig.availableEquipment,
    addEquipmentPreset,
    updateEquipmentPreset,
  ]);

  const handleDeletePreset = useCallback(
    async (id) => {
      try {
        await deleteEquipmentPreset(id);
      } catch (err) {
        console.error("Failed to delete equipment preset:", err);
      }
    },
    [deleteEquipmentPreset],
  );

  const isLoading = appState === "planning";
  const prevLoadingRef = useRef(false);
  useEffect(() => {
    if (prevLoadingRef.current && !isLoading) {
      onOpenChange(false);
    }
    prevLoadingRef.current = isLoading;
  }, [isLoading, onOpenChange]);

  const { needsApiKey, providerLabel } = useProviderStatus();

  // Fire once per opening, after initialisation has filled planConfig (the
  // first render after opening still holds the previous or empty config).
  // Without a key there is nothing to run, so the form stays up and shows
  // the key prompt instead.
  const autoFiredRef = useRef(false);
  useEffect(() => {
    if (!open) {
      autoFiredRef.current = false;
      return;
    }
    if (!autoGenerate || autoFiredRef.current || needsApiKey) return;
    if (!hasInitialisedRef.current || !planConfig.workoutTypes) return;
    autoFiredRef.current = true;
    handleGenerate({ preventDefault: () => {} });
  }, [open, autoGenerate, needsApiKey, planConfig, handleGenerate]);
  const desktop = useMediaQuery(DESKTOP_QUERY);

  const openAiSettings = useCallback(() => {
    setSettingsSheet?.("ai");
    // Close the dialog first, then swap the view on the next frame. Navigating
    // in the same commit unmounts the Radix modal layers at once, which can
    // leave `pointer-events: none` stuck on <body>; the guard restores it.
    onOpenChange(false);
    requestAnimationFrame(() => {
      onNavigate?.("settings");
      requestAnimationFrame(() => {
        if (document.body.style.pointerEvents === "none") {
          document.body.style.pointerEvents = "";
        }
      });
    });
  }, [onOpenChange, onNavigate, setSettingsSheet]);

  const locationLabel = locationOptions.find(
    (o) => o.value === planConfig.trainingLocation,
  )?.label;

  const rows = [
    {
      key: "format",
      Icon: Layers,
      label: "Format",
      value: formatSummary(planConfig),
      changed: (planConfig.format || "auto") !== "auto",
    },
    {
      key: "muscles",
      Icon: PersonStanding,
      label: "Muscles",
      value: musclesSummary(planConfig.targetAreas),
      changed: false,
    },
    {
      key: "kit",
      Icon: Dumbbell,
      label: "Kit",
      value: kitSummary(locationLabel, planConfig.availableEquipment),
      changed: false,
    },
    {
      key: "timing",
      Icon: Timer,
      label: "Warm-up",
      value: timingRowSummary(planConfig),
      changed: false,
    },
    {
      key: "notes",
      Icon: MessageSquareText,
      label: "Notes",
      value: notesSummary(planConfig.sessionNotes) || "Anything for today?",
      changed: Boolean(notesSummary(planConfig.sessionNotes)),
    },
  ];

  const BASIC_ROWS = ["kit", "notes"];
  const shownRows = showAll ? rows : rows.filter((r) => BASIC_ROWS.includes(r.key) || r.changed);

  const closeDetail = (next) => {
    if (next) return;
    if (detail === "recipe") setRecipeForm(null);
    if (detail === "kit") setPresetForm(null);
    setDetail(null);
  };

  const startSaveRecipe = () => {
    openSaveRecipe();
    setDetail("recipe");
  };

  const DETAIL_TITLES = {
    format: "Format",
    muscles: "Muscles",
    kit: "Kit",
    timing: "Warm-up & cool-down",
    notes: "Notes",
    recipe: "Save as recipe",
  };

  const detailBody = (() => {
    switch (detail) {
      case "format":
        return (
          <FormatDetail
            config={planConfig}
            onChange={(v) => handleConfigChange("format", v)}
          />
        );
      case "muscles":
        return (
          <MusclesDetail
            targetAreas={planConfig.targetAreas}
            onChange={(v) => handleConfigChange("targetAreas", v)}
          />
        );
      case "kit":
        return (
          <KitDetail
            locationOptions={locationOptions}
            location={planConfig.trainingLocation}
            onLocationChange={handleLocationChange}
            equipment={planConfig.availableEquipment}
            onEquipmentChange={(v) => handleConfigChange("availableEquipment", v)}
            isAuthenticated={isAuthenticated}
            equipmentPresets={equipmentPresets}
            presetForm={presetForm}
            onPresetName={(name) => setPresetForm((f) => ({ ...f, name }))}
            onSavePreset={handleSavePreset}
            onCancelPreset={() => setPresetForm(null)}
            onSaveAsPreset={openSaveAsPreset}
            onEditPreset={openEditPreset}
            onDeletePreset={handleDeletePreset}
          />
        );
      case "timing":
        return (
          <TimingDetail
            config={planConfig}
            onModeChange={handleSegmentModeChange}
            onWarmupMinutes={handleWarmupChange}
            onCooldownMinutes={handleCooldownChange}
          />
        );
      case "notes":
        return (
          <NotesDetail
            notes={planConfig.sessionNotes}
            onNotes={(v) => handleConfigChange("sessionNotes", v)}
            showHistory={isAuthenticated}
            useHistory={planConfig.useHistoryGuidance ?? true}
            onUseHistory={(v) => handleConfigChange("useHistoryGuidance", v)}
          />
        );
      case "recipe":
        return (
          <RecipeSaveDetail
            form={recipeForm}
            onName={(name) => setRecipeForm((f) => ({ ...f, name }))}
            onToggleField={toggleRecipeField}
            onSave={handleSaveRecipe}
            saving={savingRecipe}
          />
        );
      default:
        return null;
    }
  })();

  const pickCard = (
    <PickCard
      preview={shapePreview}
      onShuffle={handleShuffleShape}
      onHide={handleHideShape}
      pendingProgrammeSession={pendingProgrammeSession}
      onOpenProgramme={onNavigate ? handleOpenProgramme : null}
    />
  );

  const generateButton = (
    <Button
      type="submit"
      className="h-12 w-full text-base font-semibold tracking-wide shadow-[0_0_15px_hsl(var(--primary)/0.3)] transition-shadow hover:shadow-[0_0_25px_hsl(var(--primary)/0.5)]"
      disabled={needsApiKey}
    >
      <Wand2 className="mr-2 h-5 w-5 shrink-0" />
      Generate session
    </Button>
  );

  const fieldLabel = (text, hint, helpTopic) => (
    <div className="flex items-baseline justify-between gap-3">
      <span className="flex items-center gap-1 text-xs font-semibold text-muted-foreground">
        {text}
        {helpTopic && <HelpLink topic={helpTopic} label={`About ${text}`} />}
      </span>
      {hint && (
        <span className="truncate text-xs text-muted-foreground/80">{hint}</span>
      )}
    </div>
  );

  const trainForHint = (() => {
    const list = planConfig.workoutTypes || [];
    if (list.length === 0 || list.includes("next") || list.includes("mixed")) {
      return "Khronos picks the session";
    }
    return list.length > 1 ? `${list.length} goals, one block each` : null;
  })();

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!isLoading) onOpenChange(v);
      }}
    >
      <DialogContent className="flex max-h-[92dvh] w-[calc(100vw-1.5rem)] max-w-xl flex-col gap-0 overflow-hidden border-border bg-background p-0 lg:h-[min(46rem,92dvh)] lg:max-w-4xl">
        <div
          className={`shrink-0 border-b px-4 pb-3 pt-4 pr-12 transition-colors lg:px-6 ${
            scrolled ? "border-border" : "border-transparent"
          }`}
        >
          <DialogTitle className="text-lg font-semibold">
            Generate session
          </DialogTitle>
          <DialogDescription className="sr-only">
            Choose what to train and how long you have, then generate a session.
          </DialogDescription>
        </div>

        {isLoading ? (
          <div className="flex min-h-0 flex-1 flex-col px-4 pb-4 lg:px-6">
            <GenerationProgress
              isLoading={isLoading}
              generationStatus={generationStatus}
              thinkingText={thinkingText}
              takeFirstPlan={takeFirstPlan}
              cancelGeneration={cancelGeneration}
            />
          </div>
        ) : (
          <form
            onSubmit={handleGenerate}
            className="flex min-h-0 flex-1 flex-col lg:grid lg:grid-cols-[minmax(0,1fr)_20rem] lg:grid-rows-[minmax(0,1fr)]"
          >
            {/* grid-auto-rows: max-content — in a fixed-height scrolling grid,
                auto rows otherwise shrink, and the detail rows (which clip
                their overflow) collapse to nothing. */}
            <div
              onScroll={(e) => setScrolled(e.currentTarget.scrollTop > 4)}
              className="grid min-h-0 flex-1 auto-rows-max grid-cols-1 content-start gap-5 overflow-y-auto overflow-x-hidden px-4 pb-5 pt-1 lg:px-6"
            >
              {prefillSource && (
                <div className="flex items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-2.5 py-1.5 text-xs text-primary/90">
                  <Sparkles className="h-3.5 w-3.5 shrink-0" />
                  <span className="min-w-0 truncate">
                    Prefilled from {prefillSource}
                  </span>
                  {carriedOver && (
                    <button
                      type="button"
                      onClick={startFresh}
                      className="ml-auto shrink-0 font-medium text-muted-foreground transition-colors hover:text-primary"
                    >
                      Start fresh
                    </button>
                  )}
                </div>
              )}

              {/* Recipes (#78): saved sets of settings, one tap to apply. */}
              {sessionRecipes.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {sessionRecipes.map((recipe) => {
                    const isActive = recipe.id === activeRecipeId;
                    return (
                      <button
                        key={recipe.id}
                        type="button"
                        aria-pressed={isActive}
                        onClick={() => (isActive ? clearRecipe() : applyRecipe(recipe))}
                        className={`max-w-full truncate rounded-full border px-3 py-1.5 text-[13px] transition-colors ${
                          isActive
                            ? "border-primary/50 bg-primary/10 font-semibold text-primary"
                            : "border-border bg-card text-foreground hover:border-primary/40"
                        }`}
                      >
                        {isActive && (
                          <Check className="-ml-0.5 mr-1 inline h-3.5 w-3.5 align-[-2px]" />
                        )}
                        {recipe.name}
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="space-y-2">
                {fieldLabel("Train for", trainForHint, "build-a-session#train-for")}
                <TrainForPicker
                  value={planConfig.workoutTypes}
                  onChange={(v) => handleConfigChange("workoutTypes", v)}
                />
              </div>

              <div className="space-y-2">
                {fieldLabel("Time", "drag sideways, or tap + and −")}
                <DraggableStepper
                  prominent
                  momentum
                  unit="min"
                  ariaLabel="Time available"
                  id="gen-time"
                  value={planConfig.sessionDuration || 45}
                  onChange={(v) => handleConfigChange("sessionDuration", v)}
                  min={5}
                  max={180}
                  step={5}
                />
              </div>

              <div className="space-y-2">
                {fieldLabel("Energy")}
                <SegmentedToggle
                  value={planConfig.readiness || "Normal"}
                  onChange={(v) => handleConfigChange("readiness", v)}
                  options={READINESS_OPTIONS}
                  className="w-full"
                  itemClassName="flex-1 h-10 text-sm"
                />
              </div>

              {(showAll || planConfig.noFaff) && (
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <span className="flex items-center gap-1">
                      <label htmlFor="gen-no-faff" className="text-sm font-semibold text-foreground">
                        No faff
                      </label>
                      <HelpLink topic="build-a-session#no-faff" label="About No faff" />
                    </span>
                    <p className="text-xs leading-snug text-muted-foreground">
                      One or two pieces of kit, no weight changes.
                    </p>
                  </div>
                  <Switch
                    id="gen-no-faff"
                    checked={Boolean(planConfig.noFaff)}
                    onCheckedChange={(checked) => handleConfigChange("noFaff", checked)}
                  />
                </div>
              )}

              {!desktop && pickCard}

              <DetailRows rows={shownRows} onOpen={setDetail} />
              {!showAll && <MoreOptionsRow onClick={() => setRevealed(true)} />}

              {/* Saving comes after setting things up, so it sits at the end,
                  with the AI model line on phones — keeping the pinned
                  footer to just the Generate button. */}
              {showAll && (
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={startSaveRecipe}
                    className="inline-flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground transition-colors hover:text-primary"
                  >
                    <Plus className="h-4 w-4" />
                    Save these settings as a recipe
                  </button>
                  {!desktop && <ModelSwitcher onOpenSettings={openAiSettings} />}
                </div>
              )}

              {needsApiKey && (
                <div className="flex items-start gap-3 rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-3 text-sm">
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-yellow-500" />
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-yellow-500">API key needed</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {providerLabel} needs an API key to generate sessions.
                    </p>
                    <button
                      type="button"
                      onClick={openAiSettings}
                      className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-yellow-500 hover:underline"
                    >
                      <Settings className="h-3 w-3" />
                      Open AI settings
                    </button>
                  </div>
                </div>
              )}
            </div>

            {desktop ? (
              <aside className="flex min-h-0 flex-col gap-4 overflow-y-auto border-l border-border bg-card px-5 pb-5 pt-1">
                <SessionSummary
                  config={planConfig}
                  shapeName={shapePreview?.archetype?.name}
                  locationLabel={locationLabel}
                />
                {pickCard}
                <div className="flex-1" />
                {generateButton}
                {showAll && <ModelSwitcher onOpenSettings={openAiSettings} />}
              </aside>
            ) : (
              <div className="shrink-0 border-t border-border px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3">
                {generateButton}
              </div>
            )}
          </form>
        )}
      </DialogContent>

      <DetailSheet
        open={Boolean(detail)}
        onOpenChange={closeDetail}
        title={DETAIL_TITLES[detail] || ""}
      >
        {detailBody}
      </DetailSheet>
    </Dialog>
  );
};

export default GenerateSessionDialog;
